import React from 'react';

import { Box, Divider, Stack, Typography } from '@mui/material';

import { useTheme } from 'hooks/useTheme';

interface GlobalCardProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  variant?: 'default' | 'highlighted';
  iconColor?: string;
  titleColor?: string;
  dividerColor?: string;
  backgroundColor?: string;
  borderColor?: string;
  padding?: string | number;
  spacing?: number;
}

const GlobalCard: React.FC<GlobalCardProps> = ({
  icon,
  title,
  children,
  variant = 'default',
  iconColor,
  titleColor,
  dividerColor,
  backgroundColor,
  borderColor,
  padding = 2,
  spacing = 1,
}) => {
  const theme = useTheme();
  const primary = theme.theme.primary;

  const defaultColors = {
    icon: iconColor || primary,
    title: titleColor || theme.theme.text.primary,
    divider: dividerColor || primary,
    background: backgroundColor || theme.theme.background.primary,
    border: borderColor || primary,
  };

  const highlightedColors = {
    icon: iconColor || theme.theme.background.primary,
    title: titleColor || theme.theme.background.primary,
    divider: dividerColor || primary,
    background: backgroundColor || primary,
    border: borderColor || primary,
  };

  const colors = variant === 'highlighted' ? highlightedColors : defaultColors;

  return (
    <Box
      sx={{
        backgroundColor: colors.background,
        borderRadius: 2,
        padding: padding,
        minHeight: 200,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          marginBottom: spacing,
        }}
      >
        <Box
          sx={{
            color: colors.icon,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            width: 24,
            height: 24,
            '& > *': {
              fontSize: '24px !important',
              width: '24px !important',
              height: '24px !important',
            },
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="h6"
          sx={{
            color: colors.title,
            fontSize: 20,
            lineHeight: 1,
          }}
        >
          {title}
        </Typography>
      </Stack>

      <Divider
        sx={{
          backgroundColor: colors.divider,
          height: '1px',
          marginBottom: spacing,
        }}
      />

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default React.memo(GlobalCard);
